import { useState, useMemo, Fragment } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'
import useQueryGetInviteFriendReferredPlayers from '@/modules/manage-player/api/hooks/useQueryGetInviteFriendReferredPlayers'
import { expandableReferredColumns } from './constants'
import ReferralCommissionModal from '../ReferralCommissionModal'
import { GetInviteFriendReferredPlayersData } from '@/modules/manage-player/type'
import { flexRender } from '@tanstack/react-table'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import Spinner from '@/common/components/Spinner'
import { cn } from '@/common/lib/utils'

interface ExpandableReferredPlayersTableProps {
	playerId: string
	headPlayerId?: string
	depth?: number
	parentExpanded?: boolean
}

type ReferredPlayerWithExpand = GetInviteFriendReferredPlayersData & {
	hasChildren?: boolean
	isExpanded?: boolean
	depth?: number
	id?: string
}

export default function ExpandableReferredPlayersTable({
	playerId,
	headPlayerId,
	depth = 0,
	parentExpanded = true,
}: ExpandableReferredPlayersTableProps) {
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
	const [selectedPlayerForCommission, setSelectedPlayerForCommission] =
		useState<GetInviteFriendReferredPlayersData | null>(null)
	const [isCommissionModalOpen, setIsCommissionModalOpen] =
		useState<boolean>(false)

	// Use headPlayerId if provided (for tree head perspective), otherwise use current playerId
	const commissionForPlayerId = headPlayerId || playerId

	const referredPayload = {
		skip: 0,
		take: 1000,
		commissionForPlayer: commissionForPlayerId,
	}

	const { data: referredData, isLoading: isReferredLoading } =
		useQueryGetInviteFriendReferredPlayers(playerId, referredPayload, {
			enabled: parentExpanded, // Only fetch when parent is expanded (or depth 0)
		})

	const toggleRow = (playerIdToToggle: string) => {
		setExpandedRows((prev) => {
			const next = new Set(prev)
			if (next.has(playerIdToToggle)) {
				next.delete(playerIdToToggle)
			} else {
				next.add(playerIdToToggle)
			}
			return next
		})
	}

	const handleOpenCommissionModal = (
		player: GetInviteFriendReferredPlayersData,
	) => {
		setSelectedPlayerForCommission(player)
		setIsCommissionModalOpen(true)
	}

	const handleCloseCommissionModal = () => {
		setIsCommissionModalOpen(false)
		setSelectedPlayerForCommission(null)
	}

	const rowsWithExpandState = useMemo<ReferredPlayerWithExpand[]>(() => {
		if (!referredData?.data) return []

		return referredData.data.map((player) => ({
			...player,
			id: player.playerId, // Map playerId to id for compatibility
			hasChildren: true, // We assume all players can have referrals, backend will return empty if none
			isExpanded: expandedRows.has(player.playerId),
			depth,
		}))
	}, [referredData?.data, expandedRows, depth])

	const table = useReactTable({
		data: rowsWithExpandState,
		columns: expandableReferredColumns,
		getCoreRowModel: getCoreRowModel(),
	})

	if (isReferredLoading && depth === 0) {
		return <Spinner />
	}

	if (isReferredLoading && depth > 0) {
		return (
			<div className="flex items-center justify-center py-4">
				<Spinner />
			</div>
		)
	}

	if (!referredData?.data || referredData.data.length === 0) {
		if (depth === 0) {
			return (
				<div className="py-8 text-center text-muted-foreground">
					ไม่พบข้อมูลสมาชิกที่แนะนำ
				</div>
			)
		}
		return (
			<div className="py-4 text-center text-sm text-muted-foreground">
				ไม่มีสมาชิกที่แนะนำ
			</div>
		)
	}

	return (
		<>
			<Table>
				{depth === 0 && (
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<TableHead key={header.id}>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext(),
												)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
				)}
				<TableBody>
					{table.getRowModel().rows.map((row) => {
						const player = row.original
						const isExpanded = expandedRows.has(player.playerId)

						return (
							<Fragment key={row.id}>
								<TableRow
									className={cn(
										'cursor-pointer transition-colors hover:bg-muted/50',
										depth > 0 && 'bg-muted/30',
									)}
									onClick={() => toggleRow(player.playerId)}
								>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											className={cn(
												cell.column.id === 'expand' && 'w-10',
												depth > 0 && cell.column.id === 'expand' && 'pl-4',
											)}
											style={{
												paddingLeft:
													depth > 0 && cell.column.id === 'expand'
														? `${depth * 24 + 8}px`
														: undefined,
											}}
											onClick={(e) => {
												// Prevent row expansion when clicking action buttons
												if (
													cell.column.id === 'actions' ||
													cell.column.id === 'detail'
												) {
													e.stopPropagation()
												}
											}}
										>
											{cell.column.id === 'actions' ? (
												<div
													onClick={(e) => {
														e.stopPropagation()
														handleOpenCommissionModal(player)
													}}
												>
													{flexRender(
														cell.column.columnDef.cell,
														cell.getContext(),
													)}
												</div>
											) : (
												flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)
											)}
										</TableCell>
									))}
								</TableRow>
								{isExpanded && (
									<TableRow key={`${row.id}-nested`}>
										<TableCell
											colSpan={expandableReferredColumns.length}
											className="bg-muted/20 p-0"
										>
											<div className="pl-8">
												<ExpandableReferredPlayersTable
													playerId={player.playerId}
													headPlayerId={commissionForPlayerId}
													depth={depth + 1}
													parentExpanded={isExpanded}
												/>
											</div>
										</TableCell>
									</TableRow>
								)}
							</Fragment>
						)
					})}
				</TableBody>
			</Table>
			<ReferralCommissionModal
				open={isCommissionModalOpen}
				onOpenChange={handleCloseCommissionModal}
				playerData={selectedPlayerForCommission}
			/>
		</>
	)
}
